import { useToast } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TThunkDispatch } from 'shared/types/redux';
import {
  addNewbuildingToComparison,
  type IComparisonActionResult,
  removeNewbuildingFromComparison,
} from 'shared/api/comparison';
import { openAuthenticationModal } from 'shared/actions/authentication';
import { setNewbuildingComparisonStatus } from 'shared/reducers/newbuildingInformation/actions';
import { isAuthenticatedUserSelector } from 'shared/selectors/isAuthenticatedUserSelector';
import { getJsonQuery } from 'shared/selectors/getJsonQuery';
import { getNewbuildingComparisonStatus, getNewbuildingId } from 'shared/selectors/newbuildingHeader';
import { EComparisonStatus } from 'shared/types/newbuildingHeader';
import { useApplicationContext } from 'shared/utils/applicationContext';

import { CompareButtonOnboarding } from './CompareButtonOnboarding';
import { CompareButtonWithTooltip } from './CompareButtonWithTooltip';
import { CompareFlightOnboardingContent } from './CompareFlightOnboardingContent';
import { CompareOnboardingContent } from './CompareOnboardingContent';
import { CompareToastContent } from './CompareToastContent';
import { COMPARE_ENABLED_CONFIG } from './constants';
import {
  trackAddNewbuildingToComparison,
  trackCompareFlightTooltipAboutClick,
  trackCompareFlightTooltipShow,
  trackRemoveNewbuildingFromComparison,
} from './tracking';
import { useCompareOnboarding } from './useCompareOnboarding';
import { NewbuildingCompareFlightModalContainer } from '../NewbuildingCompareFlightModal';

const COMPARISON_TOAST_HIDE_DELAY = 5000;
const COMPARISON_ERROR_TEXT = 'Не удалось изменить сравнение. Попробуйте еще раз';

interface INewbuildingHeaderCompareButtonProps {
  withOnboarding?: boolean;
}

export const NewbuildingHeaderCompareButton: React.FC<INewbuildingHeaderCompareButtonProps> = ({
  withOnboarding = false,
}) => {
  const { config, httpApi, logger } = useApplicationContext();
  const dispatch = useDispatch<TThunkDispatch>();
  const newbuildingId = useSelector(getNewbuildingId) as number;
  const isAuthenticated = useSelector(isAuthenticatedUserSelector);
  const comparisonStatus = useSelector(getNewbuildingComparisonStatus);
  const jsonQuery = useSelector(getJsonQuery);
  const toast = useToast({ placement: 'bottom-right', hideDelay: COMPARISON_TOAST_HIDE_DELAY, size: 'L' });
  const isCompareEnabled = config.get<boolean>(COMPARE_ENABLED_CONFIG) || false;

  const isAdded = comparisonStatus === EComparisonStatus.Added;
  const isVisible = isCompareEnabled && comparisonStatus != null && comparisonStatus !== EComparisonStatus.Unavailable;

  const { open, isFlight, closeHandler } = useCompareOnboarding(withOnboarding && isVisible);
  const [isFlightModalOpen, setIsFlightModalOpen] = React.useState(false);

  React.useEffect(() => {
    if (open && isFlight) {
      trackCompareFlightTooltipShow(jsonQuery);
    }
    // Показ трекаем один раз на открытие тултипа, jsonQuery берём актуальный на этот момент
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isFlight]);

  const handleFlightButtonClick = React.useCallback(() => {
    trackCompareFlightTooltipAboutClick(jsonQuery);
    closeHandler();
    setIsFlightModalOpen(true);
  }, [closeHandler, jsonQuery]);

  const handleFlightModalClose = React.useCallback(() => {
    setIsFlightModalOpen(false);
  }, []);

  const showComparisonToast = React.useCallback(
    (result: IComparisonActionResult) => {
      toast.open({
        type: result.success === false ? 'error' : 'success',
        hasCloseIcon: false,
        content: <CompareToastContent text={result.text} comparisonUrl={result.comparisonUrl} />,
      });
    },
    [toast],
  );

  const handleClick = React.useCallback(async () => {
    const nextValue = !isAdded;

    if (nextValue && !isAuthenticated) {
      dispatch(openAuthenticationModal({ autoRefresh: true }));

      return;
    }

    dispatch(setNewbuildingComparisonStatus(nextValue ? EComparisonStatus.Added : EComparisonStatus.Available));

    try {
      const result = nextValue
        ? await addNewbuildingToComparison({ httpApi, logger, newbuildingId })
        : await removeNewbuildingFromComparison({ httpApi, logger, newbuildingId });

      showComparisonToast(result);

      if (result.success === false) {
        dispatch(setNewbuildingComparisonStatus(nextValue ? EComparisonStatus.Available : EComparisonStatus.Added));

        return;
      }

      if (nextValue) {
        trackAddNewbuildingToComparison(newbuildingId);
      } else {
        trackRemoveNewbuildingFromComparison(newbuildingId);
      }
    } catch (_error) {
      dispatch(setNewbuildingComparisonStatus(nextValue ? EComparisonStatus.Available : EComparisonStatus.Added));
      showComparisonToast({ success: false, text: COMPARISON_ERROR_TEXT });
    }
  }, [dispatch, isAdded, isAuthenticated, httpApi, logger, newbuildingId, showComparisonToast]);

  if (!isVisible) {
    return null;
  }

  const button = <CompareButtonWithTooltip isAdded={isAdded} onClick={handleClick} />;

  const onboardingContent = isFlight ? (
    <CompareFlightOnboardingContent onButtonClick={handleFlightButtonClick} />
  ) : (
    <CompareOnboardingContent />
  );

  return (
    <>
      {withOnboarding && open ? (
        <CompareButtonOnboarding open={open} content={onboardingContent} onClose={closeHandler}>
          {button}
        </CompareButtonOnboarding>
      ) : (
        button
      )}

      {isFlightModalOpen && <NewbuildingCompareFlightModalContainer onClose={handleFlightModalClose} />}
    </>
  );
};
